import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import useApi from "../hooks/useApi";
import SessionService from "../services/SessionService";
import { useCerberus } from "../hooks/useCerberus";
import { Role } from "../components/Access/Role";
import { useBranding } from "../branding/BrandingProvider";

interface SubscriptionContextType {
  hasActiveSubscription: boolean;
  subscriptionPlan: string | null;
  nextBillingDate: string | null;
  autoRenew: boolean;
  isLoading: boolean;
  checkSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined,
);

export function SubscriptionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);
  const [nextBillingDate, setNextBillingDate] = useState<string | null>(null);
  const [autoRenew, setAutoRenew] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const api = useApi();
  const sessionService = SessionService.getInstance();
  const { userHasRole } = useCerberus();
  const brand = useBranding();

  const checkSubscription = useCallback(async () => {
    // Don't bother checking if we know the user isn't logged in.
    if (!sessionService.isAuthenticated()) {
      setHasActiveSubscription(false);
      setSubscriptionPlan(null);
      setNextBillingDate(null);
      setAutoRenew(false);
      setIsLoading(false);
      return;
    }

    // Only check subscriptions for users with the Patient role
    if (!userHasRole(Role.Patient)) {
      setHasActiveSubscription(false);
      setSubscriptionPlan(null);
      setNextBillingDate(null);
      setAutoRenew(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      // Use real API call to check subscription status
      const response = await api.get("/subscription/status");
      setHasActiveSubscription(response.data.hasActiveSubscription);

      // If there's an active subscription, get the details
      if (response.data.hasActiveSubscription) {
        const detailsResponse = await api.get("/subscription");
        setSubscriptionPlan(
          detailsResponse.data.planName || brand.carePlanName,
        );
        setNextBillingDate(detailsResponse.data.currentPeriodEnd);
        setAutoRenew(detailsResponse.data.autoRenew || false);
      } else {
        setSubscriptionPlan(null);
        setNextBillingDate(null);
        setAutoRenew(false);
      }
    } catch (error) {
      console.error("Error checking subscription:", error);
      setHasActiveSubscription(false);
      setSubscriptionPlan(null);
      setNextBillingDate(null);
      setAutoRenew(false);
    } finally {
      setIsLoading(false);
    }
  }, [api, sessionService, userHasRole, brand.carePlanName]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  return (
    <SubscriptionContext.Provider
      value={{
        hasActiveSubscription,
        subscriptionPlan,
        nextBillingDate,
        autoRenew,
        isLoading,
        checkSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider",
    );
  }
  return context;
}
